import { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import Link from '@/components/common/Link';

import type { MegaMenuLink } from './megaMenuView.types';
import { iconButtonBaseSx } from './megaMenuOverlay.constants';

const mobileNavTitleSx = {
	color: 'common.white'
} as const;

const MOBILE_ACCORDION_TRIGGER_SX = {
	...iconButtonBaseSx,
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 4,
	py: 4,
	pr: '34px',
	pl: 0,
	boxSizing: 'border-box',
	textAlign: 'left',
	background: 'none',
	border: 'none',
	cursor: 'pointer',
	color: 'inherit',
	'&:focusVisible': {
		outline: '2px solid rgba(255, 255, 255, 0.55)',
		outlineOffset: 4
	}
} as const;

type MobileAccordionSectionProps = {
	title: string;
	items: MegaMenuLink[];
	sectionKey: string;
	panelId: string;
	isExpanded: boolean;
	onToggle: () => void;
	transition: Record<string, unknown>;
};

const MobileAccordionSection = ({
	title,
	items,
	sectionKey,
	panelId,
	isExpanded,
	onToggle,
	transition
}: MobileAccordionSectionProps) => {
	const theme = useTheme();

	return (
		<Fragment>
			<Box
				component="button"
				type="button"
				aria-expanded={isExpanded}
				aria-controls={panelId}
				onClick={onToggle}
				sx={MOBILE_ACCORDION_TRIGGER_SX}
			>
				<Typography component="span" variant="nav" sx={mobileNavTitleSx}>
					{title}
				</Typography>
				<Typography
					component="span"
					variant="nav"
					aria-hidden
					sx={{
						...mobileNavTitleSx,
						flexShrink: 0,
						width: '1ch',
						textAlign: 'center'
					}}
				>
					{isExpanded ? '−' : '+'}
				</Typography>
			</Box>
			<AnimatePresence initial={false}>
				{isExpanded ? (
					<motion.div
						id={panelId}
						key={panelId}
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={transition}
						style={{ overflow: 'hidden' }}
					>
						<Stack component="nav" aria-label={title} spacing={7} sx={{ pl: 10, pt: 4, pb: 8 }}>
							{items.map((item) => (
								<Link key={`${sectionKey}-${item.label}`} href={item.href} passHref>
									<Typography component="span" variant="subnav" sx={{ color: 'common.white' }}>
										{item.label}
									</Typography>
								</Link>
							))}
						</Stack>
					</motion.div>
				) : null}
			</AnimatePresence>
			<Box
				sx={{
					borderTop: `1px solid ${alpha(theme.palette.common.white, 0.18)}`
				}}
			/>
		</Fragment>
	);
};

export default MobileAccordionSection;
